import * as BetsAPI from "app/api/betsApi";
import ErrorBoundary from "app/ErrorBoundary/ErrorBoundary";
import { BetSlipHelper } from "app/helpers/betSlipHelper";
import BetDrawerLogic from "app/logic/betDrawerLogic";
import CompetitionLogic from "app/logic/competitionLogic";
import type { BetSlipType, BetType } from "app/types/betSlipType";
import type { EventMarketType, EventRunnerType } from "app/types/eventType";
import { BetSlipStates, EventStatus } from "app/utils/const";
import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import React, { Component, lazy, Suspense } from "react";
import { intlShape } from "react-intl";
import { connect } from "react-redux";
import { $Values } from "utility-types";
import "./BetSlip.scss";
const BetSlipContent: React.LazyExoticComponent<any> = lazy(
  () => import("./components/BetSlipContent/BetSlipContent")
);
const BetSlipCompleted: React.LazyExoticComponent<any> = lazy(
  () => import("./components/BetSlipCompleted/BetSlipCompleted")
);
type Props = {
  betSlip: BetSlipType;
  removeBetSlip: (...args: Array<any>) => any;
  updateBetSlip: (...args: Array<any>) => any;
  subscribeEvent: (...args: Array<any>) => any;
  subscribeMarkets: (...args: Array<any>) => any;
  subscribeRunner: (...args: Array<any>) => any;
  intl: $Values<typeof intlShape>;
  isIdle: boolean;
  latest: boolean;
  loadCurrentBetsCount: (...args: Array<any>) => any;
  isWebsocketReady: boolean;
  getBetMarketId: (...args: Array<any>) => any;
  getBetRunnerId: (...args: Array<any>) => any;
  subscribeFailedBetRunner: (...args: Array<any>) => any;
  subscribeFailedBetMarket: (...args: Array<any>) => any;
  updateBetSlipPrices: (...args: Array<any>) => any;
};
type State = {
  isValidatingBetSlip: boolean;
  validateBetSlipIntervalId: any;
};

class BetSlip extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isValidatingBetSlip: false,
      validateBetSlipIntervalId: 0,
    };
  }

  componentDidMount() {
    const { betSlip } = this.props;
    const betSlipMarket = BetSlipHelper.getBetSlipMarket(betSlip);
    // removes finished betSlips
    // on e.g. refresh of site
    (betSlip.state === BetSlipStates.FINISHED ||
      betSlip.status === EventStatus.CLOSED) &&
      this.props.removeBetSlip(betSlip);

    // if the bet slip is not finished and
    // the event is not closed and
    if (
      betSlip.state !== BetSlipStates.FINISHED &&
      betSlip.status !== EventStatus.CLOSED &&
      !isEmpty(betSlipMarket)
    ) {
      if (this.props.isWebsocketReady) {
        this.subscribeToWebsockets();
      } else {
        // if the websocket is not ready yet
        // then let's try again in a bit
        setTimeout(() => {
          this.subscribeToWebsockets();
        }, 100);
      }
    }
  }

  componentWillUnmount() {
    // Clear intervals
    clearInterval(this.state.validateBetSlipIntervalId);
    this.setState({
      isValidatingBetSlip: false,
    });
  }

  stopPolling = () => {
    this.setState({
      isValidatingBetSlip: false,
    });
    clearInterval(this.state.validateBetSlipIntervalId);
  };

  componentDidUpdate(prevProps, prevState) {
    // if the bet slip state changed to pending or processing
    // and the bet slip doesn't have a bet id
    // and validation of the bet hasn't started yet
    // then start validating the bet via API
    // just in case the websockets fail
    if (
      (prevProps.betSlip.state === BetSlipStates.PENDING ||
        prevProps.betSlip.state === BetSlipStates.PROCESSING) &&
      this.props.betSlip.betId != null &&
      !this.state.isValidatingBetSlip
    ) {
      const validateBetSlipIntervalId = setInterval(() => {
        // extra check if status failed stop polling
        this.props.betSlip.state === BetSlipStates.FAILED && this.stopPolling();
        // if status finished stop polling
        this.props.betSlip.state === BetSlipStates.FINISHED &&
          this.stopPolling();

        if (this.props.betSlip.state === BetSlipStates.FAILED) {
          const marketId = this.getBetMarketId();
          const runnerId = this.getBetRunnerId();
          this.subScribeFailedMarketAndRunner(marketId, runnerId);
        }

        this.validateBetSlip();
      }, 5000);
      this.setState({
        validateBetSlipIntervalId,
        isValidatingBetSlip: true,
      });
    } else if (
      this.props.betSlip.state === BetSlipStates.FAILED &&
      prevProps.betSlip.state === BetSlipStates.PENDING &&
      this.props.betSlip.betId != null &&
      this.state.isValidatingBetSlip
    ) {
      const marketId = this.getBetMarketId();
      const runnerId = this.getBetRunnerId();
      this.subScribeFailedMarketAndRunner(marketId, runnerId);
      // BET-606
      delete this.props.betSlip.stake;
      delete this.props.betSlip["requestedStake"];
      clearInterval(this.state.validateBetSlipIntervalId);
      this.setState({
        isValidatingBetSlip: false,
      });
    }

    // if the bet slip state has changed to finished, then
    // clear the polling intervals
    if (
      this.props.betSlip.state === BetSlipStates.FINISHED &&
      prevProps.betSlip.state !== this.props.betSlip.state
    ) {
      clearInterval(this.state.validateBetSlipIntervalId);
      // update no. of open bets
      this.props.loadCurrentBetsCount();
      this.setState({
        isValidatingBetSlip: false,
      });
    }
  }

  // BET-3885 subscribe external failed bets market and runner for logging on api
  subScribeFailedMarketAndRunner = (marketId: any, runnerId: any) => {
    this.props.subscribeFailedBetRunner(runnerId, this.props.betSlip.betId);
    this.props.subscribeFailedBetMarket(marketId, this.props.betSlip.betId);
  };
  // get selected price's market id (for ubscribe failed market)
  getBetMarketId = () => {
    const betSlipMarket: EventMarketType | null =
      BetSlipHelper.getBetSlipMarket(this.props.betSlip);
    return betSlipMarket && betSlipMarket.id;
  };
  // get selected price's runner id (for ubscribe failed runner)
  getBetRunnerId = () => {
    const betSlipOdd: EventRunnerType | null = BetSlipHelper.getBetSlipOdd(
      this.props.betSlip
    );
    return betSlipOdd && betSlipOdd.id;
  };

  /**
   * removes betSlip if state is finished, if the event is closed or if there are no markets
   */
  checkAndRemoveBetSlip = () => {
    const { betSlip } = this.props;
    const betSlipMarket: EventMarketType | null =
      BetSlipHelper.getBetSlipMarket(betSlip);

    if (
      (betSlip.state != null && betSlip.state === BetSlipStates.FINISHED) ||
      betSlip.status === EventStatus.CLOSED ||
      isEmpty(betSlipMarket)
    ) {
      // Clear intervals
      clearInterval(this.state.validateBetSlipIntervalId);
      this.setState({
        isValidatingBetSlip: false,
      });
      //remove the bet slip
      this.props.removeBetSlip(betSlip.localId);
    }
  };
  // Validate the bet up against the API
  validateBetSlip = () => {
    const { betSlip } = this.props;

    if (betSlip.betId) {
      BetsAPI.validateBetSlip(betSlip.betId)
        .then((bet: BetType) => {
          BetDrawerLogic.updateBetSlipFromBet(bet);
          if (bet.affectedRunnerProfitAndLoss) {
            CompetitionLogic.updateRunnerPNL(bet.affectedRunnerProfitAndLoss);
          }
        })
        .catch(error => {
          throw error;
        });
    }
  };
  subscribeToWebsockets = () => {
    const { betSlip } = this.props;
    const betSlipMarket = BetSlipHelper.getBetSlipMarket(betSlip);

    if (betSlipMarket != null) {
      const eventId = this.props.betSlip.id;
      // subscribing to each runner instead of the market
      // because subscribing to the market only gets best price updates
      const runnerId =
        betSlipMarket.runners &&
        betSlipMarket.runners &&
        betSlipMarket.runners[0].id;
      this.props.subscribeEvent(eventId);
      // this.props.subscribeMarkets(marketId);
      runnerId && this.props.subscribeRunner(runnerId);
    }
  };
  toggleCollapsible = () => {
    this.props.betSlip.isActive = !this.props.betSlip.isActive;
    this.props.updateBetSlip(this.props.betSlip);
  };
  renderTeamName = (teamName: string) => {
    return (
      <div className="bet-slip__header__team-name">
        <span>{teamName}</span>
      </div>
    );
  };
  resetBetSlip = () => {
    delete this.props.betSlip.state;
    delete this.props.betSlip.stake;
    delete this.props.betSlip["requestedStake"];
    clearInterval(this.state.validateBetSlipIntervalId);
    this.setState({
      isValidatingBetSlip: false,
    });
    this.forceUpdate();
  };
  shouldComponentUpdate = (nextProps, nextState) => {
    // if betslip open and bet finsished
    // update open bet no.
    if (
      nextProps.betSlip.state === "FINISHED" &&
      !this.props.betSlip === "FINISHED"
    ) {
      this.props.loadCurrentBetsCount();
    }

    return true;
  };

  resetAnimation() {
    const { betSlip } = this.props;
    betSlip.latest = false;
    this.props.updateBetSlip(betSlip);
  }

  render() {
    const { betSlip, user } = this.props;
    const betSlipMarket: EventMarketType | null =
      BetSlipHelper.getBetSlipMarket(betSlip);

    // If the market is null or empty then don't render it
    if (isEmpty(betSlipMarket)) {
      return null;
    }

    const isFinished = betSlip.state === BetSlipStates.FINISHED;
    return (
      <div
        className={cx("bet-slip", {
          "bet-slip--latest": betSlip.latest,
        })}
        onAnimationEnd={() => this.resetAnimation()}
      >
        {isFinished ? (
          <Suspense fallback={<div className="fallback">loading...</div>}>
            <BetSlipCompleted
              betSlip={betSlip}
              resetBetSlip={this.resetBetSlip}
            />
          </Suspense>
        ) : (
          <Suspense fallback={<div className="fallback">loading...</div>}>
            {user.preferences?.priceViewMode && (
              <ErrorBoundary>
                <BetSlipContent
                  betSlip={betSlip}
                  subscribeFailedBetMarket={this.props.subscribeFailedBetMarket}
                  subscribeFailedBetRunner={this.props.subscribeFailedBetRunner}
                />
              </ErrorBoundary>
            )}
          </Suspense>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isWebsocketReady: state.subscribeModel.isWebsocketReady,
  isIdle: state.sessionModel.isIdle,
  user: state.userModel.user,
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    removeBetSlip: betSlipId =>
      dispatch.betSlipListModel.removeBetSlip(betSlipId),
    updateBetSlip: betSlip => dispatch.betSlipListModel.updateBetSlip(betSlip),
    subscribeEvent: (eventId: number) =>
      dispatch.subscribeModel.subscribeEvent({
        eventId,
      }),
    subscribeRunner: (runnerId: number) =>
      dispatch.subscribeModel.subscribeRunner({
        runnerId,
      }),
    loadCurrentBetsCount: () =>
      dispatch.betSlipListModel.loadCurrentBetsCount(),
    subscribeFailedBetRunner: (runnerId: number, betId: number) =>
      dispatch.subscribeModel.subscribeFailedBetRunner({
        runnerId,
        betId,
      }),
    subscribeFailedBetMarket: (marketId: number, betId: number) =>
      dispatch.subscribeModel.subscribeFailedBetMarket({
        marketId,
        betId,
      }),
    updateBetSlipPrices: prices =>
      dispatch.betSlipListModel.updateBetSlipPrices(prices),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BetSlip) as any;
